import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import KpiCard from '../../components/common/KpiCard';
import BarChart from '../../components/common/BarChart';
import ProgressBar from '../../components/common/ProgressBar';
import { iniciais } from '../../utils/nome';
import { formatMesAbrev } from '../../utils/date';
import { onKeyActivate } from '../../utils/teclado';
import { primeiroNome, saudacao } from './dashboardHelpers';

export default function AbaVisaoGeralDashboard({ hospitais, conformidades, coberturas, avaliacoesPorHospital, historicos }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { naoConformidades, acoesVencidas, acoesVencendo } = useMemo(() => {
    let naoConf = 0;
    let vencidas = 0;
    let vencendo = 0;
    Object.values(avaliacoesPorHospital).forEach((avaliacoes) => {
      avaliacoes.forEach((a) => {
        if (a.status === 'nao_conforme' || a.status === 'parcialmente_conforme') naoConf += 1;
        if (a.status_acao === 'vencido') vencidas += 1;
        if (a.status_acao === 'a_vencer') vencendo += 1;
      });
    });
    return { naoConformidades: naoConf, acoesVencidas: vencidas, acoesVencendo: vencendo };
  }, [avaliacoesPorHospital]);

  const valores = Object.values(conformidades);
  const aderenciaMedia = valores.length ? Math.round(valores.reduce((a, b) => a + b, 0) / valores.length) : 0;

  const tendenciaCarteira = useMemo(() => {
    const series = Object.values(historicos).filter((h) => h.length > 0);
    if (series.length === 0) return [];
    const nMeses = Math.max(...series.map((s) => s.length));
    return Array.from({ length: nMeses }, (_, i) => {
      const valoresDoMes = series.map((s) => s[i]?.percentual).filter((v) => v !== undefined);
      const media = valoresDoMes.length ? valoresDoMes.reduce((a, b) => a + b, 0) / valoresDoMes.length : 0;
      const mesRef = series.find((s) => s[i])?.[i]?.mes_referencia;
      return { label: formatMesAbrev(mesRef), valor: Math.round(media * 10) / 10 };
    });
  }, [historicos]);

  const ranking = useMemo(() => {
    return hospitais
      .map((h) => {
        const historico = historicos[h.id_hospital] || [];
        const atual = conformidades[h.id_hospital] ?? 0;
        const anterior = historico.length >= 2 ? historico[historico.length - 2].percentual : null;
        const delta = anterior === null ? null : Math.round((atual - anterior) * 10) / 10;
        return { hospital: h, atual, delta };
      })
      .sort((a, b) => a.atual - b.atual)
      .slice(0, 5);
  }, [hospitais, conformidades, historicos]);

  return (
    <div>
      <div className="welcome-banner">
        <div>
          <div className="welcome-banner__eyebrow">ACF Moura · Consultoria</div>
          <div className="welcome-banner__title">{saudacao()}, {primeiroNome(user.nome)} 👋</div>
          <div className="welcome-banner__subtitle">
            {hospitais.length} hospital(is) em acompanhamento · {naoConformidades} fragilidade(s) em aberto
          </div>
        </div>
        <button className="am-btn am-btn-dark" onClick={() => navigate('/hospitais')}>
          <span className="ms">local_hospital</span>
          Ver hospitais
        </button>
      </div>

      <div className="kpi-grid mb-3">
        <KpiCard icon="local_hospital" value={hospitais.length} label="Hospitais ativos" />
        <KpiCard icon="trending_up" value={`${aderenciaMedia}%`} label="Aderência média" />
        <KpiCard icon="pending_actions" value={naoConformidades} label="Fragilidades em aberto" />
        <KpiCard icon="schedule" value={acoesVencendo} label="Ações corretivas vencendo" />
        <KpiCard icon="error" value={acoesVencidas} label="Ações corretivas vencidas" />
      </div>

      {hospitais.length > 0 && (
        <div className="d-flex flex-wrap gap-3 mb-3">
          <div className="card-soft" style={{ padding: 24, flex: '2 1 420px' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--am-tinta)', marginBottom: 4 }}>
              Comparação entre hospitais
            </div>
            <p style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)', margin: '0 0 4px' }}>
              % de conformidade geral por hospital{hospitais.length > 8 ? ' (8 primeiros)' : ''}
            </p>
            <BarChart
              dados={hospitais.slice(0, 8).map((h) => ({ label: h.nome, valor: conformidades[h.id_hospital] ?? 0 }))}
            />
          </div>

          {tendenciaCarteira.length > 0 && (
            <div className="card-soft" style={{ padding: 24, flex: '2 1 420px' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--am-tinta)', marginBottom: 4 }}>
                Tendência da carteira
              </div>
              <p style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)', margin: '0 0 4px' }}>
                Média de % de conformidade de todos os hospitais, mês a mês
              </p>
              <BarChart dados={tendenciaCarteira} corBarra="var(--am-link)" />
            </div>
          )}

          <div className="card-soft" style={{ padding: 24, flex: '1 1 280px' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--am-tinta)', marginBottom: 4 }}>
              Prioridade de atenção
            </div>
            <p style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)', margin: '0 0 12px' }}>
              Hospitais com menor % de conformidade
            </p>
            <div className="d-flex flex-column gap-2">
              {ranking.map(({ hospital, atual, delta }) => (
                <div
                  key={hospital.id_hospital}
                  role="button"
                  tabIndex={0}
                  className="list-row"
                  style={{ padding: '8px 10px' }}
                  onClick={() => navigate(`/hospitais/${hospital.id_hospital}`)}
                  onKeyDown={onKeyActivate(() => navigate(`/hospitais/${hospital.id_hospital}`))}
                >
                  <div className="flex-grow-1" style={{ minWidth: 0, fontSize: 13, fontWeight: 600, color: 'var(--am-tinta)' }}>
                    {hospital.nome}
                  </div>
                  {delta !== null && delta !== 0 && (
                    <span
                      className="d-inline-flex align-items-center"
                      style={{ fontSize: 11, fontWeight: 700, color: delta > 0 ? 'var(--am-sucesso)' : 'var(--am-critico)' }}
                      title="Variação vs. mês anterior"
                    >
                      <span className="ms" style={{ fontSize: 15 }}>{delta > 0 ? 'arrow_upward' : 'arrow_downward'}</span>
                      {Math.abs(delta)}
                    </span>
                  )}
                  <span style={{ width: 40, textAlign: 'right', fontSize: 13, fontWeight: 700, color: 'var(--am-tinta)' }}>
                    {atual}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="card-soft" style={{ padding: 24 }}>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--am-tinta)' }}>Carteira de instituições</div>
          <span
            role="button"
            tabIndex={0}
            style={{ fontSize: 13, fontWeight: 600, color: 'var(--am-link)', cursor: 'pointer' }}
            onClick={() => navigate('/hospitais')}
            onKeyDown={onKeyActivate(() => navigate('/hospitais'))}
          >
            Ver todas
          </span>
        </div>

        {hospitais.length === 0 ? (
          <div className="empty-state">
            <div className="ms">local_hospital</div>
            <div>Nenhum hospital cadastrado ainda.</div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {hospitais.slice(0, 6).map((h) => {
              const pct = conformidades[h.id_hospital];
              const cob = coberturas[h.id_hospital];
              return (
                <div
                  key={h.id_hospital}
                  className="list-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/hospitais/${h.id_hospital}`)}
                  onKeyDown={onKeyActivate(() => navigate(`/hospitais/${h.id_hospital}`))}
                >
                  <div className="avatar-square">{iniciais(h.nome)}</div>
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--am-tinta)' }}>{h.nome}</div>
                    <div style={{ fontSize: 12, color: 'var(--am-cinza-texto-2)' }}>
                      {h.cnpj || 'CNPJ não informado'}
                    </div>
                  </div>
                  {pct !== undefined && (
                    <>
                      <div style={{ width: 200 }}>
                        <ProgressBar percentual={pct} />
                        {cob && (
                          <div style={{ fontSize: 11, color: 'var(--am-cinza-texto-2)', marginTop: 3 }}>
                            {cob.avaliados}/{cob.total} avaliados
                          </div>
                        )}
                      </div>
                      <div style={{ width: 44, textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--am-tinta)' }}>
                        {pct}%
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
